import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { IAppState } from "../core/redux/IAppState";

/**
 * How long a search result may back a bulk edit.
 *
 * An edit sends the rule ids the grid was holding, so a "replace" only deletes what
 * the search saw — a rule another session added since survives, making the result a
 * merge rather than the replace the review dialog showed.
 */
export const SEARCH_RESULTS_MAX_AGE_MS = 10 * 60 * 1000;

/**
 * Whether the rules in the grid are too old to act on.
 *
 * From the store, not component state: dialogs must read this themselves, because
 * showModalAction freezes their props at open time and a popped-out dialog shares the
 * store but not the page's tree.
 */
export const useSearchResultFreshness = (
  maxAgeMs: number = SEARCH_RESULTS_MAX_AGE_MS,
): { resultsStale: boolean } => {
  const loadedAt = useSelector(
    (s: IAppState) => s.liftAccess?.liftAccessRuleSetsLoadedAt,
  );
  const [resultsStale, setResultsStale] = useState(false);

  // One timer to the expiry rather than polling the clock.
  useEffect(() => {
    if (loadedAt == null) {
      setResultsStale(false);
      return;
    }

    const remainingMs = maxAgeMs - (Date.now() - loadedAt);

    if (remainingMs <= 0) {
      setResultsStale(true);
      return;
    }

    setResultsStale(false);
    const timer = setTimeout(() => setResultsStale(true), remainingMs);

    return () => clearTimeout(timer);
  }, [loadedAt, maxAgeMs]);

  return { resultsStale };
};
